import React, { useRef } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faGear } from '@fortawesome/free-solid-svg-icons/faGear';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import Link from 'buyside/components/presentation/Link/link';
import NavigationNotification from 'buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationNotifications/navigationNotification';
import NavigationNotificationLoading from 'buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationNotifications/navigationNotificationLoading';

import { notificationsSettingUrl, notificationsUrl } from 'buyside/util/urlHelper';
import useDropdownButton from 'buyside/util/hooks/useDropdownButton';

import {
  CurrentUserNotificationsQuery,
  useClickedNotificationsMutation,
  useCurrentUserNotificationsQuery,
  useViewAllNotificationsMutation
} from 'interfaces/graphql-typings';

import styles from './navigationNotificationsStyles.scss';

const NavigationNotifications: React.FC = () => {
  const { dropdownRef, dropdownVisible, toggleDropdown } = useDropdownButton();

  const scrollArea = useRef<HTMLDivElement>(null);

  const {
    data,
    loading: notificationsLoading,
    error,
    fetchMore
  } = useCurrentUserNotificationsQuery({
    variables: { first: 8 },
    notifyOnNetworkStatusChange: true
  });

  const [
    viewAllNotifications,
    { loading: viewAllNotificationsLoading }
  ] = useViewAllNotificationsMutation();

  const [clickedNotifications] = useClickedNotificationsMutation();

  const { notifications, id: currentUserId, newNotificationsCount, unreadNotificationsCount } =
    data?.currentUser || {};

  const loadMoreEntries = (): Promise<ApolloQueryResult<CurrentUserNotificationsQuery>> =>
    fetchMore({
      variables: {
        cursor: notifications?.pageInfo.endCursor,
        first: 8
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const currentUser = fetchMoreResult?.currentUser;
        const newEdges = currentUser?.notifications?.edges;

        if (!currentUser || !newEdges?.length || !currentUser.notifications?.pageInfo)
          return previousResult;

        return {
          currentUser: {
            ...currentUser,
            notifications: {
              edges: [...(previousResult.currentUser?.notifications?.edges || []), ...newEdges],
              pageInfo: currentUser.notifications.pageInfo
            }
          }
        };
      }
    });

  const handleScroll = (): void => {
    if (scrollArea.current) {
      const { scrollTop, scrollHeight, offsetHeight } = scrollArea.current;

      const halfHeight = (scrollHeight - offsetHeight) / 3;
      if (
        !notificationsLoading &&
        !error &&
        scrollHeight - scrollTop - offsetHeight <= halfHeight
      ) {
        if (notifications?.pageInfo.hasNextPage) loadMoreEntries();
      }
    }
  };

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <button
        data-heap="notifications"
        data-testid="notifications-dropdown-button"
        className={classNames(styles.dropdownButton, {
          [styles.active]: dropdownVisible
        })}
        onClick={(): void => {
          clickedNotifications();
          toggleDropdown();
        }}
      >
        <div className="fa-layers">
          <FontAwesomeIcon icon={faBell} />
          {Boolean(newNotificationsCount) && newNotificationsCount && newNotificationsCount > 0 && (
            <span className="fa-layers-counter">
              {newNotificationsCount > 100 ? '99+' : newNotificationsCount}
            </span>
          )}
        </div>
      </button>
      <div
        className={classNames(styles.dropdownAreaWrapper, {
          [styles.visible]: dropdownVisible
        })}
      >
        <div className={styles.dropdownArea}>
          <div className={styles.dropdownHeader}>
            Notifications
            <Button
              analyticsTag="mark-all-notifications-read"
              disabled={!unreadNotificationsCount}
              isLoading={viewAllNotificationsLoading}
              onClick={viewAllNotifications}
              text="Mark All Notifications Read"
              type={BUTTON_TYPES.LINK}
            />
          </div>
          <div className={styles.notificationList} ref={scrollArea} onScroll={handleScroll}>
            {!notificationsLoading && !notifications?.edges?.length && (
              <div className={styles.notificationListEmpty}>No Notifications</div>
            )}
            {notifications?.edges?.map((edge, idx) =>
              edge?.node && currentUserId ? (
                <NavigationNotification
                  key={`${edge.node.id}-${idx}`}
                  notification={edge.node}
                  currentUserId={currentUserId}
                />
              ) : null
            )}
            {notificationsLoading &&
              [1, 2, 3].map(idx => (
                <NavigationNotificationLoading key={`navigation-notification-loading-${idx}`} />
              ))}
          </div>
          <div className={styles.dropdownActionArea}>
            <Link analyticsTag="view-all-notifications" to={notificationsUrl()}>
              <FontAwesomeIcon icon={faBell} />
              View All Notifications
            </Link>
            <Link analyticsTag="adjust-notifications-settings" to={notificationsSettingUrl()}>
              <FontAwesomeIcon icon={faGear} />
              Adjust Notification Settings
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationNotifications;
